function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
let prvw_main_cart_items = [];

// variant tags for each store
const default_variants_tags = [
  {
    targetVariantParentSelector: "variant-radios",
    targetInputSelector: "fieldset",
    targetSelectSelector: null
  },
  {
    targetVariantParentSelector: "variant-selects",
    targetInputSelector: "fieldset",
    targetSelectSelector: null
  },
  {
    targetVariantParentSelector: "variant-selects",
    targetInputSelector: null,
    targetSelectSelector: ".product-form__input--dropdown"
  },
  {
    targetVariantParentSelector: ".product-form__controls-group",
    targetInputSelector: null,
    targetSelectSelector: ".selector-wrapper.product-form__item"
  },
  {
    targetVariantParentSelector: "#AddToCartForm--product-template",
    targetInputSelector: ".radio-wrapper.product-form__item fieldset",
    targetSelectSelector: null
  },
  {
    targetVariantParentSelector: ".dbtfy-color-swatches",
    targetInputSelector: ".swatch",
    targetSelectSelector: null
  }
];

const accessibleThemes = [
  "dawn",
  "sense",
  "taste",
  "refresh",
  "ride",
  "studio",
  "spotlight",
  "publisher",
  "craft",
  "crave",
  "debut (vintage theme)",
  "theme-export-connecy-uk-com-live-connecy-uk-1"
];
const userThemeName = window.Shopify.theme.name.toLowerCase();

const prvw_tags = [
  {
    insertBefore: '[data-icon="gpicon-product-cartbutton"]',
    quantityInput: '[data-icon="gpicon-product-quantity"]'
  },
  {
    insertBefore: ".gp-product-button",
    quantityInput: "gp-product-quantity"
  },
  {
    insertBefore: "product-form",
    quantityInput: ".product-form__quantity"
  },
  {
    insertBefore: '[data-pf-type="ProductATC"]',
    quantityInput: '[data-pf-type="ProductQuantity"]'
  },
  {
    insertBefore: ".product-single__add-to-cart",
    quantityInput: "[data-quantity-input]"
  },
  {
    insertBefore: ".product-info__buy-buttons",
    quantityInput: ".product-info__quantity-selector"
  },
  {
    insertBefore: ".product-form__quantity-submit",
    quantityInput: null
  },
  {
    insertBefore: ".product-form__item--submit",
    quantityInput: 'label[for="Quantity-product-template"]'
  },
  {
    insertBefore: ".ProductForm__BuyButtons, .ProductForm__AddToCart",
    quantityInput: ".ProductForm__QuantitySelector"
  },
  {
    insertBefore: "[data-block-type='buy-buttons']",
    quantityInput: "[data-block-type='quantity-selector']"
  },
  {
    insertBefore: ".product-page--submit-action",
    quantityInput: ".quantity-controls__outer"
  },
  {
    insertBefore: ".product-form__payment-container",
    quantityInput: ".product-form__info-item--quantity"
  },
  {
    insertBefore: ".ecom-product-single__add-to-cart",
    quantityInput: ".ecom-product-single__quantity"
  },
  {
    insertBefore: ".product-info__add-to-cart",
    quantityInput: ".quantity-input"
  },
  {
    insertBefore: "[data-product-submit]",
    quantityInput: ".product-quantity-label, .product-quantity-input"
  },

  {
    insertBefore: ".product-form--atc",
    quantityInput: ".product-form--atc-qty"
  },
  {
    insertBefore: ".purchase-details",
    quantityInput: ".purchase-details__quantity"
  },
  {
    insertBefore: ".product-single__form .payment-buttons",
    quantityInput: ".product__quantity"
  },
  {
    insertBefore: ".product-form--wide",
    quantityInput: ".product-single__quantity"
  },
  {
    insertBefore: ".product-single__add-to-cart",
    quantityInput: null
  },
  {
    insertBefore: ".product-form--button-container",
    quantityInput: ".product-form__item--quantity"
  },
  {
    insertBefore: ".js-product-add-to-cart",
    quantityInput: null
  },
  { insertBefore: ".product-detail__form__action", quantityInput: null },
  { insertBefore: ".product__submit__buttons", quantityInput: null },
  {
    insertBefore: ".buy-buttons-row",
    quantityInput: 'label[for="quantity"], .quantity-wrapper'
  },
  {
    insertBefore: ".t4s-product-form__buttons",
    quantityInput: "[data-quantity-wrapper]"
  },
  {
    insertBefore: ".qty-wrapper--with-payment-button",
    quantityInput: ".product-qty"
  },
  {
    insertBefore: ".shopify-product-form",
    quantityInput: ".product-quantity-block"
  },
  {
    insertBefore: ".shopify-product-form",
    quantityInput: ".product-block-quantity-selector"
  },
  {
    insertBefore: ".type_buy_buttons",
    quantityInput: ".type_quantity_selector"
  },
  {
    insertBefore: ".product-single__form .add-to-cart",
    quantityInput: "#shopify_quantity"
  },
  { insertBefore: ".purchase-section", quantityInput: ".quantity.form" },
  {
    insertBefore: ".product-form__buttons",
    quantityInput: ".quantity_selector"
  },
  { insertBefore: ".product__atc", quantityInput: ".quantity--input" },
  {
    insertBefore: ".product-form__payment-container",
    quantityInput: ".quantity-selector"
  },
  {
    insertBefore: ".product-add-to-cart-container",
    quantityInput: ".m-product-form--input:has(m-quantity-input)"
  },
  {
    insertBefore: ".product__block__buttons",
    quantityInput: ".product__block__quantity"
  },
  {
    insertBefore: ".x-buy-button",
    quantityInput: ".x-quantity-input"
  },
  {
    insertBefore: '[data-instant-action-type="redirect-to-cart"]',
    quantityInput:
      '[data-instant-type="container"]:has(> .instant-quantity-input)'
  },
  {
    insertBefore: ".pro-detail-button",
    quantityInput: ".product-quantity-action"
  },
  {
    insertBefore: ".cst-small-crtbtn",
    quantityInput: null
  },
  {
    insertBefore: ".product-single__shopify-payment-btn",
    quantityInput: ".selector-wrapper"
  },
  {
    insertBefore: ".yv-checkout-btn-full-width",
    quantityInput: ".yv-product-quantity"
  },
  {
    insertBefore: ".yv-checkout-btn",
    quantityInput: 'input[name="updates[]"].quantity-input__input'
  },
  {
    insertBefore: ".product-form-product-template",
    quantityInput: null
  },
  {
    insertBefore: ".btn-cart-wrapper",
    quantityInput: null
  },
  {
    insertBefore: ".add-to-cart-container",
    quantityInput: null
  },
  {
    insertBefore: "#AddToCart-product-template",
    quantityInput: ".product__quantity--button"
  },
  {
    insertBefore: ".single_product__quantity",
    quantityInput: ".quantity_input"
  },
  {
    insertBefore: ".product-form__submit",
    quantityInput: ".product__quantity-wrapper"
  },
  {
    insertBefore: ".btn--add-to-cart",
    quantityInput: ".qtybox"
  },
  {
    insertBefore: '[data-pf-type="ProductATC2"]',
    quantityInput: "product-quantity"
  },
  {
    insertBefore: ".add-to-cart",
    quantityInput: null
  },
  {
    insertBefore: "product-buy-buttons-element",
    quantityInput: null
  },
  {
    insertBefore: "[data-aid=product-form-submit-button][fdprocessedid]",
    quantityInput: null
  },
  {
    insertBefore: ".variations_button:has(.single_add_to_cart_button)",
    quantityInput: null
  },
  {
    insertBefore: "hdt-buy-buttons .hdt-product-form__submit",
    quantityInput: null
  },
  {
    insertBefore: ".prd-block_actions:has(.js-add-to-cart)",
    quantityInput: null
  },
  {
    insertBefore: "product-payment-container",
    quantityInput: null
  },
  {
    insertBefore: "gp-product-button",
    quantityInput: null
  }
];

const loader_classes_bundle = ["loading-overlay__spinner"];

(function () {
  try {
    let prvw_variant_tags = [{ variantInput: ".product-form__option" }];

    const getQueryParam = () =>
      new URLSearchParams(window.location.search).get("prvw_app");
    function logDebug(message, data = null) {
      if (getQueryParam()?.toString() === "debug") {
        console.log("[ Pumper ]: ", message, data);
      }
      // console.log("[ Pumper ]: ", message, data);
    }

    console.log(
      `%c 🚀 Pumper app initialized! https://apps.shopify.com/pumper-quantity-breaks-product-bundles-discounts?utm_source=productLed&utm_medium=consoleLog&utm_campaign=productLed&utm_id=${Shopify.shop}`,
      "background: white; color: orange; padding: 8px; font-size: 16px; border-radius: 8px;"
    );

    console.log(
      "https://apps.shopify.com/pumper-quantity-breaks-product-bundles-discounts"
    );

    let prvw_length_bar = document.getElementById("prvw_length_bar").value;
    let productId = document.getElementById("pumperProductID").value;
    let pageSelectors = document.querySelectorAll("pumper-bundle");
    let prvw_forms = [];

    let prvw_custom_form_selector = document.getElementById(
      "prvw_custom_form_selector"
    ).value;

    let insertAfterSelector = document.getElementById(
      "pumper_insert_after_btn"
    ).value;

    if (insertAfterSelector === "true") {
      let marginTopQB = document.getElementById(
        "prvw_custom_settings_marginTopQB"
      ).value;

      if (marginTopQB != null) {
        document.querySelector(".prvw_block").style.marginTop =
          marginTopQB + "px";
      } else {
        document.querySelector(".prvw_block").style.marginTop = "12px";
      }
    }

    function generateUniqueId() {
      const timestamp = new Date().getTime();
      const randomNumber = Math.floor(Math.random() * 1000000);
      const uniqueId = timestamp + "-" + randomNumber;
      return uniqueId;
    }

    function removeQuantityInput() {
      for (let i = 0; i < prvw_tags.length; i++) {
        let quantityInput = document.querySelector(prvw_tags[i].quantityInput);
        if (quantityInput) {
          logDebug(
            "removing the quantity input element selector: " +
              prvw_tags[i].quantityInput
          );
          quantityInput.remove();
          break;
        }
      }
    }

    function addCustomSelectorContainer() {
      let containerPosition;
      if (insertAfterSelector === "true") {
        containerPosition = "afterend";
      } else {
        containerPosition = "beforebegin";
      }

      if (pageSelectors.length > 0) {
        pageSelectors.forEach((pageSelector) => {
          pageSelector.innerHTML = bundle.innerHTML;

          selectFormNearestToWidget(pageSelector);
        });

        let defaultSelectedBar = document.getElementById(
          "prvw_default_selected"
        ).value;

        if (defaultSelectedBar !== "0" && defaultSelectedBar !== 0) {
          document.querySelector(
            `.prvw__quantity_input${defaultSelectedBar}`
          ).checked = true;
        }

        for (let i = 1; i <= prvw_length_bar; i++) {
          document
            .querySelectorAll(`.prvw__quantity_input${i}`)
            .forEach((item) => {
              let tempId = generateUniqueId();
              item.id = tempId;

              let labelElement = item.nextElementSibling;
              labelElement.setAttribute("for", tempId);
            });
        }

        document.querySelector("#prvw_bundle")?.remove();
      } else if (!prvw_custom_selector) {
        for (let i = 0; i < prvw_tags.length; i++) {
          // add not condition for forms inside <payment-terms> tag
          let insertBefore = document.querySelector(
            ":not(payment-terms) > " + prvw_tags[i].insertBefore
          );

          selectFormNearestToWidget(insertBefore);

          if (
            insertBefore &&
            !insertBefore.classList.contains("pmpr_bundle_form")
          ) {
            insertBefore.insertAdjacentHTML(
              containerPosition,
              bundle.innerHTML
            );
            insertBefore.style.marginTop = "8px";

            break;
          }
        }
      } else {
        let insertBefore = document.querySelector(prvw_custom_selector);
        insertBefore.insertAdjacentHTML(containerPosition, bundle.innerHTML);
        insertBefore.style.marginTop = "8px";

        selectFormNearestToWidget(insertBefore);
      }
    }

    /*/////////////////////
    we don't want to insert our quantity input in all the forms present in the page
    so below function is used to identify the nearest form possible to our widget 
    /////////////////////*/

    function selectFormNearestToWidget(element) {
      try {
        if (prvw_custom_form_selector !== "") {
          prvw_forms = document.querySelectorAll(prvw_custom_form_selector);
          return;
        }

        if (window.pmpr_select_every_form === true) {
          prvw_forms = document.querySelectorAll("form[action='/cart/add']");
          if (!prvw_forms.length) {
            prvw_forms = document.querySelectorAll("form[action*='/cart/add']");
          }
        } else {
          let closestForm = null;
          let rootRoute = window.Shopify.routes.root;
          let formSelector = `form[action='${rootRoute}cart/add']`;

          if (
            element.tagName === "FORM" &&
            element.getAttribute("action") === `${rootRoute}cart/add`
          ) {
            // check whether the element is a form itself
            closestForm = element;
          } else {
            //  find the form within or closest to the element
            closestForm =
              element.querySelector(formSelector) ||
              element.closest(formSelector);
          }

          formSelector = `product-form > form[action='${rootRoute}cart/add']`;

          if (!closestForm) {
            let parentElement = element.parentElement;
            closestForm =
              parentElement.querySelector(formSelector) ||
              parentElement.closest(formSelector);
          }

          if (!closestForm) {
            selectFormNearestToWidget(element.parentElement);
          }

          if (closestForm) {
            prvw_forms.push(closestForm);
          }
        }
      } catch (error) {
        console.error("🔴🔴 Error in selectFormNearestToWidget:", error);
      }
    }

    // -----------------------
    // no need because, not checking accessibleThemes
    // -----------------------
    // function removeDefaultVariantOptions() {
    //   if (checkIfVariantExists()) {
    //     for (let e of document.getElementsByTagName("variant-radios")) {
    //       if (!accessibleThemes.includes(userThemeName)) {
    //         // console.log('removing from dom ')
    //         e.remove(); // ask why we do this
    //       }
    //     }
    //   }
    // }

    function showHeaderFooterLines() {
      let prvw_hide_header_footer_lines = document.getElementById(
        "prvw_hide_header_footer_lines"
      ).value;
      if (prvw_hide_header_footer_lines == "true") {
        const root = document.querySelector(":root");
        root.style.setProperty(
          "--pseudo-prvw-header-footer-line-visibility",
          "none"
        );
      }
    }

    // theme(Exclusive) pseudo class color setting for most popular tag.
    function dynamicTagBackgroundColor() {
      const tagBackgroundColor = document.getElementById(
        "prvw_tag_background_color"
      ).value;
      const root = document.querySelector(":root");
      root.style.setProperty(
        "--pseudo-tag-background-color",
        tagBackgroundColor
      );
    }

    const root = document.querySelector(":root");

    // set dynamic height to hidden card of theme(Packy)
    const cardBrderRadius = document.getElementById("prvw_border_radius").value;
    const hiddenCardDynamicHeight = `calc(100% - ${
      2 * Number(cardBrderRadius)
    }px)`;

    root.style.setProperty(
      "--packy-hidden-card-height",
      hiddenCardDynamicHeight
    );

    // set dyanmic tag border-left and right color
    const labelBackgroundColor = document.getElementById(
      "prvw_background_color"
    ).value;
    root.style.setProperty(
      "--template3-tag-border-color",
      labelBackgroundColor
    );

    const bundle = document.getElementById("prvw_bundle");

    if (!document.getElementById("prvw_custom_var_selector")) {
      logDebug(
        "prvw_custom_var_selector not found",
        document.getElementById("prvw_custom_var_selector")
      );
      return;
    }

    if (performance.navigation.type == 2) {
      location.reload(true);
    }

    let prvw_custom_selector = document.getElementById(
      "prvw_custom_var_selector"
    ).value;

    removeQuantityInput();
    addCustomSelectorContainer();

    bundle.remove();

    // removeDefaultVariantOptions();

    showHeaderFooterLines();

    dynamicTagBackgroundColor();

    /** New js */

    // checking if we have to add header footer lines or not

    function checkIfVariantExists() {
      let prvw_include_variant = document.getElementById(
        "prvw_include_variant"
      ).value;

      let prvw_has_only_default_variant = document.getElementById(
        "prvw_has_only_default_variant"
      ).value;

      if (
        prvw_include_variant == "true" &&
        prvw_has_only_default_variant != "true"
      ) {
        return true;
      } else {
        return false;
      }
    }
    let prvw_border_color = document.getElementById("prvw_border_color").value;
    let prvw_background_color_non_selected = document.getElementById(
      "prvw_background_color_non_selected"
    ).value;

    let prvw_background_color = document.getElementById(
      "prvw_background_color"
    ).value;
    let prvw_border_width = document.getElementById("prvw_border_width").value;
    let prvw_border_radius =
      document.getElementById("prvw_border_radius").value;
    let prvw_block_title_size = document.getElementById(
      "prvw_block_title_size"
    ).value;
    let prvw_bottom_title_size = document.getElementById(
      "prvw_bottom_title_size"
    ).value;
    let prvw_show_price_by_each = document.getElementById(
      "prvw_show_price_by_each"
    ).value;
    let prvw_money_format = document.getElementById("prvw_money_format").value;
    let prvw_dynamic_discount_string = document.getElementById(
      "prvw_dynamic_discount"
    ).innerHTML;

    let gemPageButton = document.querySelector("gp-product-button");
    const skipToCheckout = document.getElementById(
      "pumper_skip_to_checkout"
    ).value;

    let prvw_dynamic_discount = prvw_dynamic_discount_string
      .substring(0, prvw_dynamic_discount_string.length - 1)
      .replaceAll(/\s/g, "")
      .split(",")
      .map(Number);

    prvw_dynamic_discount.pop();
    let prvw_dynamic_price_string =
      document.getElementById("prvw_dynamic_price").innerHTML;
    let prvw_dynamic_price = prvw_dynamic_price_string
      .substring(0, prvw_dynamic_price_string.length - 1)
      .replaceAll(/\s/g, "")
      .split(",");
    prvw_dynamic_price.pop();
    let prvw_dynamic_quantity_string = document.getElementById(
      "prvw_dynamic_quantity"
    ).innerHTML;
    let prvw_dynamic_quantity = prvw_dynamic_quantity_string
      .substring(0, prvw_dynamic_quantity_string.length - 1)
      .replaceAll(/\s/g, "")
      .split(",");
    prvw_dynamic_quantity.pop();
    let variant_vals = document.getElementById("prvw_variant_vals").value;

    /** end new js */

    let prvw_id_state;
    let prvw_value_state;
    let prvw_i_state;
    let variant_values = "";
    let prvw_variant_map = {};
    let isVariantNotAvailable = false;
    let prvw_form_id_itr = 0;
    let prvw_variant_map2 = {};

    if (!checkIfVariantExists()) {
      prvw_forms.forEach(function (form) {
        let form_id = form.getAttribute("id");
        if (!form_id) {
          form.setAttribute("id", "prvw_form_" + prvw_form_id_itr);
          form_id = "prvw_form_" + prvw_form_id_itr;
          prvw_form_id_itr++;
        }

        if (gemPageButton) {
          form.querySelector("input[type=hidden][name=quantity]")?.remove();
        }

        let input = document.createElement("input");
        input.type = "hidden";
        input.name = "quantity";
        input.id = "prvw_custom_quantity_clone_" + form_id;
        input.classList.add("prvw_custom_quantity_clone_" + form_id);
        input.value = "1";
        input.setAttribute("form", form_id);
        form.prepend(input);
      });
    }

    let insertInputInForm = (item) => {
      document.querySelector(".product-variant-id")?.remove();
      let prevData = item;

      prvw_forms.forEach(function (form) {
        form.querySelector("input[type=hidden][name=id]")?.remove();
        form.querySelector("input[type=hidden][name=quantity]")?.remove();
        form
          .querySelectorAll("select[name=id]")
          ?.forEach((item) => item?.remove());

        form.querySelectorAll(".pmpr_input")?.forEach((item) => {
          item?.remove();
        });

        item.forEach((item, index) => {
          // console.log(item, "item");
          let id_input = document.createElement("input");
          id_input.type = "hidden";
          id_input.classList.add("pmpr_input");
          id_input.value = item.id;

          let q_input = document.createElement("input");
          q_input.type = "hidden";
          q_input.classList.add("pmpr_input");
          q_input.value = item.quantity;

          let id_name;
          let q_name;

          if (index == 0 || window?.mu_version) {
            id_name = "id";
            q_name = "quantity";
          } else {
            id_name = `items[${index - 1}][id]`;
            q_name = `items[${index - 1}][quantity]`;
          }

          id_input.name = id_name;
          q_input.name = q_name;

          prevData = prevData.map((data) => {
            if (data.id == id_input.value) {
              return {
                ...data,
                name: id_name
              };
            }
            return data;
          });

          form.prepend(id_input);
          form.prepend(q_input);
        });
      });

      // setTimeout(() => {
      //   if (accessibleThemes.includes(userThemeName)) {
      //     let isOurVariantsExists = checkIfVariantExists();
      //     if (isOurVariantsExists) {
      //       delayCheckImageVariant(prevData);
      //     }
      //   }
      // }, 1000);
    };

    function handleVariantImageChange(e) {
      try {
        let fieldsArray = [];
        let defaultVariantId;
        let deafultVariantTagName;

        for (let tag of default_variants_tags) {
          const variantElement = document.querySelector(
            tag.targetVariantParentSelector
          );

          console.log("🟠 variantElement", variantElement);
          if (variantElement) {
            defaultVariantId = variantElement.getAttribute("id");
            deafultVariantTagName = variantElement.tagName.toLowerCase();

            let isOurVariantsExists = checkIfVariantExists();
            console.log("🟠 isOurVariantsExists", isOurVariantsExists);
            if (isOurVariantsExists) {
              if (variantElement.tagName.toLowerCase() !== "form") {
                variantElement.style.display = "none";
              }
              if (variantElement.tagName.toLowerCase() === "form") {
                let radioWrapper =
                  variantElement.querySelectorAll(".radio-wrapper");
                radioWrapper.forEach((item) => (item.style.display = "none"));
              }
            }

            const parentElement = variantElement.parentElement; // Access the parent element
            const observer = new MutationObserver((mutationsList) => {
              for (const mutation of mutationsList) {
                if (mutation.type === "childList") {
                  // Loop through added nodes
                  mutation.addedNodes.forEach((node) => {
                    // Check if the node is an element and matches the 'variant-selects' selector
                    if (
                      node.nodeType === Node.ELEMENT_NODE &&
                      node.matches(
                        `${deafultVariantTagName}#${defaultVariantId}`
                      )
                    ) {
                      // Check some condition to decide whether to set display none or block
                      if (isOurVariantsExists) {
                        node.style.display = "none";
                      } else {
                        node.style.display = "block";
                      }
                    }
                  });
                }
              }
            });
            observer.observe(parentElement, { childList: true, subtree: true });

            let all_fieldsets = variantElement.querySelectorAll(
              tag.targetInputSelector
            );
            let all_selectFields = variantElement.querySelectorAll(
              tag.targetSelectSelector
            );

            let variantId = e?.target?.id;

            if (variantId === undefined) {
              variantId = `prvw_selector_${Number(prvw_value_state) - 1}_1`;
            }

            const selectedFieldsArray = [];
            let all_selected_fields = document.querySelectorAll(
              `#${variantId}`
            );
            all_selected_fields.forEach((item, index) => {
              selectedFieldsArray[index] = item.value;
            });

            if (all_fieldsets.length > 0) {
              all_fieldsets.forEach((item) => {
                let inputFields = item.querySelectorAll("input");
                fieldsArray = fieldsArray.concat(Array.from(inputFields));
              });

              selectedFieldsArray.forEach((selectedItem) => {
                fieldsArray.forEach((defaultItem) => {
                  if (selectedItem === defaultItem.value) {
                    defaultItem.click();
                  }
                });
              });
              break;
            } else if (all_selectFields.length > 0) {
              all_selectFields.forEach((item) => {
                let selectFields = item.querySelectorAll("select");
                fieldsArray = fieldsArray.concat(Array.from(selectFields));
              });

              selectedFieldsArray.forEach((selectedItem) => {
                fieldsArray.forEach((selectField) => {
                  let optionsArray = Array.from(selectField.options);
                  optionsArray.forEach((option) => {
                    let targetValue = option.value;
                    if (selectedItem == targetValue) {
                      selectField.value = selectedItem;
                      const event = new Event("change", { bubbles: true });
                      selectField.dispatchEvent(event);
                    }
                  });
                });
              });
              break;
            }
          } else {
            console.warn(
              `Element not found for selector: ${tag.targetVariantParentSelector}`
            );
          }
        }
      } catch (error) {
        console.error("variant image not working", error);
      }
    }

    function matchVariantImageOnLoad() {
      try {
        let tempObj = {};
        for (const item in prvw_variant_map2) {
          tempObj[item] = prvw_variant_map2[item].id;
        }

        const selectContainer = document.querySelectorAll(
          `.prvw_selector_${Number(prvw_value_state) - 1}`
        );

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const id = urlParams.get("variant");
        let variant_combo_name;
        if (id) {
          for (const [key, value] of Object.entries(tempObj)) {
            if (value === id) {
              variant_combo_name = key;
            }
          }
          let variant_arr = variant_combo_name.split("^-^");
          const iterationTimes = selectContainer.length / variant_arr.length;

          let iterationCount = 0;
          for (let i = 0; i < iterationTimes; i++) {
            for (let j = 0; j < variant_arr.length; j++) {
              selectContainer[iterationCount].value = decodeHTML(
                variant_arr[j]
              );
              iterationCount++;
            }
          }
        }
      } catch (error) {
        console.error("Error matching variant image on load", error);
      }
    }

    function delayCheckImageVariant(prevData) {
      let currTargetElement = document.querySelector('.pmpr_input[name="id"]');
      let currTargetValue = currTargetElement.value;
      let prevElement = prevData.find((item) => item.name === "id");

      // console.log(prevElement.id, "previous ID");
      // console.log(currTargetElement.value, "current ID");

      if (prevElement && prevElement.id !== currTargetValue) {
        // console.log("Both ID is different, Revert to Prvious id");
        prvw_forms.forEach(function (form) {
          form.querySelectorAll('.pmpr_input[name="id"]').forEach((item) => {
            let parent = item.parentNode;
            let nextSibling = item.nextSibling; // Get the next sibling of the item

            item.remove(); // Remove the old element

            let id_input = document.createElement("input");
            id_input.type = "hidden";
            id_input.classList.add("pmpr_input");
            id_input.value = prevElement.id;
            id_input.name = "id";

            // Insert the new element at the same location where the old element was
            parent.insertBefore(id_input, nextSibling);
          });
        });
      }
    }

    function prvw_radio_control(id, value, pageBuildID, variantChange, e) {
      prvw_value_state = value;
      prvw_id_state = id;
      prvw_i_state = value - 1;
      let activeLabel;

      // *********************************************** //
      // variant image change functionality
      // if (accessibleThemes.includes(userThemeName)) {
      handleVariantImageChange(e);
      // }

      // for variant change we don't want the below two condition to execute
      if (!variantChange) {
        for (let i = 1; i <= prvw_length_bar; i++) {
          document
            .querySelectorAll(`.prvw__quantity_label${i}`)
            .forEach((item) => {
              item.style = "";
              item.style.backgroundColor = prvw_background_color_non_selected;
              item.style.borderRadius = prvw_border_radius + "px";
            });

          document.querySelectorAll(`.prvw__radio_btn${i}`).forEach((item) => {
            item.style.backgroundColor = prvw_border_color;
            item.style.boxShadow = "0 0 0 1px " + prvw_border_color;
          });
        }

        document.querySelectorAll(".block__cbmain--radio").forEach((item) => {
          item.style = "";
        });

        let labelElement = document.getElementById(pageBuildID);
        labelElement.checked = true;
        activeLabel = labelElement.nextElementSibling;

        let radioBtn = activeLabel.querySelector(".block__cbmain--radio");

        radioBtn.style.backgroundColor = prvw_border_color;
        radioBtn.style.boxShadow = "0 0 0 1px " + prvw_border_color;

        activeLabel.style.borderColor = prvw_border_color;
        activeLabel.style.backgroundColor = prvw_background_color;
        activeLabel.style.borderWidth = prvw_border_width + "px";

        // if (accessibleThemes.includes(userThemeName)) {
        // console.log('yes included theme')
        // let isOurVariantsExists = checkIfVariantExists();
        // if (isOurVariantsExists) {
        // console.log('match variant is running')
        // }
        // }
        // matchVariantImageOnLoad();
      }

      let block_title_line_height =
        prvw_block_title_size > 24 ? prvw_block_title_size / 30 : "";
      let bottom_title_line_height =
        prvw_bottom_title_size > 9 ? prvw_bottom_title_size / 7 : "";
      document.getElementById("prvw__block_title").style.lineHeight =
        block_title_line_height;
      document.getElementById("prvw__bottom_title").style.lineHeight =
        bottom_title_line_height;

      logDebug("before variant specific code in js file");
      if (checkIfVariantExists()) {
        prvw_main_cart_items = [];
        let map = new Map();
        let iter = 0;
        let totalOriginalAmount = 0;
        let totalComparePrice = 0;
        let variantUnavailabiltyFlag = false;

        if (variantChange) {
          activeLabel = pageBuildID.closest("label");
        } else {
          let labelElement = document.getElementById(pageBuildID);
          activeLabel = labelElement.nextElementSibling;
        }

        activeLabel
          .querySelectorAll(".prvw_selector_" + (value - 1))
          .forEach(function (item, index) {
            variant_values += prvw_escape_html(item.value);

            let currOption = prvw_variant_map[variant_values];

            if (iter == variant_vals - 1) {
              if (
                currOption?.compare_at_price == null ||
                currOption.compare_at_price == ""
              ) {
                totalComparePrice += parseFloat(currOption?.price / 100);
              } else {
                totalComparePrice += parseFloat(
                  currOption.compare_at_price / 100
                );
              }
              totalOriginalAmount += parseFloat(currOption?.price / 100);

              iter = 0;
              if (map.has(currOption?.id)) {
                map.set(currOption?.id, map.get(currOption?.id) + 1);
              } else {
                map.set(currOption?.id, 1);
              }
              if (!currOption) {
                logDebug("current option is null", variant_values);
                logDebug("variant map", prvw_variant_map);
                variantUnavailabiltyFlag = variantUnavailabiltyFlag || true;
              }
              variant_values = "";
            } else {
              iter += 1;
            }
          });

        if (variantUnavailabiltyFlag) {
          isVariantNotAvailable = true;
          // const row = document.querySelectorAll(
          //   `.prvw_selector_${prvw_value_state - 1}_${i + 1}`
          // );
        } else {
          isVariantNotAvailable = false;
        }
        logDebug("totalOriginalAmount: " + totalOriginalAmount);
        logDebug("totalComparePrice: " + totalComparePrice);
        if (totalOriginalAmount) {
          if (prvw_show_price_by_each == "true") {
            if (prvw_dynamic_price[prvw_i_state] === "amount") {
              setTotalPrice(
                formatAmount(
                  (totalOriginalAmount -
                    prvw_dynamic_discount[prvw_i_state] *
                      window.Shopify.currency.rate) /
                    prvw_dynamic_quantity[prvw_i_state]
                )
              );
            } else if (prvw_dynamic_price[prvw_i_state] === "percentage") {
              setTotalPrice(
                formatAmount(
                  (totalOriginalAmount -
                    (totalOriginalAmount *
                      prvw_dynamic_discount[prvw_i_state]) /
                      100) /
                    prvw_dynamic_quantity[prvw_i_state],
                  "percentage"
                )
              );
            } else if (prvw_dynamic_price[prvw_i_state] === "each") {
              let discount =
                (
                  window.Shopify.currency.rate *
                  prvw_dynamic_discount[prvw_i_state]
                ).toFixed(2) * prvw_dynamic_quantity[prvw_i_state];
              setTotalPrice(
                formatAmount(
                  (totalOriginalAmount - discount) /
                    prvw_dynamic_quantity[prvw_i_state]
                )
              );
            } else if (prvw_dynamic_price[prvw_i_state] === "fixed") {
              setTotalPrice(
                formatAmount(
                  prvw_dynamic_discount[prvw_i_state] *
                    window.Shopify.currency.rate
                )
              );
            } else {
              setTotalPrice(
                formatAmount(
                  totalOriginalAmount / prvw_dynamic_quantity[prvw_i_state]
                )
              );
            }
          } else {
            if (prvw_dynamic_price[prvw_i_state] === "amount") {
              setTotalPrice(
                formatAmount(
                  totalOriginalAmount -
                    prvw_dynamic_discount[prvw_i_state] *
                      window.Shopify.currency.rate
                )
              );
            } else if (prvw_dynamic_price[prvw_i_state] === "percentage") {
              logDebug("totalOriginalAmount: " + totalOriginalAmount);
              logDebug(
                "prvw_dynamic_discount[prvw_i_state]: " +
                  prvw_dynamic_discount[prvw_i_state]
              );
              logDebug(
                "prvw_dynamic_discount[prvw_i_state] / 100: " +
                  prvw_dynamic_discount[prvw_i_state] / 100
              );
              logDebug(
                "totalOriginalAmount * prvw_dynamic_discount[prvw_i_state] / 100: " +
                  (totalOriginalAmount * prvw_dynamic_discount[prvw_i_state]) /
                    100
              );

              setTotalPrice(
                formatAmount(
                  totalOriginalAmount -
                    (totalOriginalAmount *
                      prvw_dynamic_discount[prvw_i_state]) /
                      100,
                  "percentage"
                )
              );
            } else if (prvw_dynamic_price[prvw_i_state] === "each") {
              let discount =
                (
                  window.Shopify.currency.rate *
                  prvw_dynamic_discount[prvw_i_state]
                ).toFixed(2) * prvw_dynamic_quantity[prvw_i_state];

              setTotalPrice(formatAmount(totalOriginalAmount - discount));
            } else if (prvw_dynamic_price[prvw_i_state] === "fixed") {
              setTotalPrice(
                formatAmount(
                  prvw_dynamic_discount[prvw_i_state] *
                    window.Shopify.currency.rate
                )
              );
            } else {
              setTotalPrice(formatAmount(totalOriginalAmount));
            }
          }
        }
        let totalValue = totalOriginalAmount;

        if (document.getElementById("bottomtitle__total__price")) {
          if (prvw_dynamic_price[prvw_i_state] === "amount") {
            totalValue =
              totalOriginalAmount -
              prvw_dynamic_discount[prvw_i_state] *
                window.Shopify.currency.rate;
          } else if (prvw_dynamic_price[prvw_i_state] === "percentage") {
            totalValue =
              totalOriginalAmount -
              (totalOriginalAmount * prvw_dynamic_discount[prvw_i_state]) / 100;
          } else if (prvw_dynamic_price[prvw_i_state] === "each") {
            let discount =
              (
                window.Shopify.currency.rate *
                prvw_dynamic_discount[prvw_i_state]
              ).toFixed(2) * prvw_dynamic_quantity[prvw_i_state];

            totalValue = totalOriginalAmount - discount;
          } else if (prvw_dynamic_price[prvw_i_state] === "fixed") {
            totalValue =
              prvw_dynamic_discount[prvw_i_state] *
              window.Shopify.currency.rate;
          }

          let formatType = prvw_dynamic_price[prvw_i_state];

          document
            .querySelectorAll(".bottomtitle__totalPrice__container")
            .forEach((item) => {
              item.innerHTML = formatAmount(totalValue, formatType);
            });
        }

        for (let [key, value] of map) {
          prvw_main_cart_items.push({
            id: key,
            quantity: value
          });
        }

        if (skipToCheckout !== "true" && !window.upcartMoneyFormat) {
          try {
            const activeSelectors = document.querySelectorAll(
              `#${e?.target?.id}`
            );

            let activeSelectorId = [];
            activeSelectors.forEach((selector) =>
              activeSelectorId.push(selector.value)
            );

            // console.log("kriua 3", activeSelectorId.join(""));

            let activeId;
            if (e?.target?.value) {
              activeId = prvw_variant_map[activeSelectorId.join("")].id;

              prvw_main_cart_items = [
                ...prvw_main_cart_items.filter((item) => item.id === activeId),
                ...prvw_main_cart_items.filter((item) => item.id !== activeId)
              ];
            }
            // console.log("active id, cart items", activeId, prvw_main_cart_items);
          } catch (error) {
            console.log(error);
          }

          // console.log("active id, cart items", activeId, prvw_main_cart_items);
          insertInputInForm(prvw_main_cart_items);
        }

        isVarientStockEmpty();
        //change compare at price
        if (document.querySelector("#prvw_originalAmount_" + prvw_i_state)) {
          let prvw_customCompareAtPrice = document.getElementById(
            "prvw_customCompareAtPrice"
          ).value;

          if (
            prvw_customCompareAtPrice == "" ||
            prvw_customCompareAtPrice == "0"
          ) {
            if (totalComparePrice != totalValue) {
              document
                .querySelectorAll(".prvw__pb__originalAmount" + prvw_i_state)
                .forEach((item) => {
                  item.innerHTML = formatAmount(totalComparePrice);
                });
            }
          } else {
            const compareAtPrice = formatAmount(
              prvw_customCompareAtPrice *
                window.Shopify.currency.rate *
                prvw_dynamic_quantity[prvw_i_state]
            );
            const totalCompareAtPrice = formatAmount(totalValue);
            if (compareAtPrice != totalCompareAtPrice) {
              document
                .querySelectorAll(".prvw__pb__originalAmount" + prvw_i_state)
                .forEach((item) => {
                  item.innerHTML = compareAtPrice;
                });
            }
          }
        }
      } else {
        logDebug("before without variant specic code in js file");
        prvw_forms.forEach((form, index) => {
          let quantityClone = document.querySelectorAll(
            ".prvw_custom_quantity_clone_" + form.getAttribute("id")
          );
          if (quantityClone.length > 0) {
            quantityClone.forEach((item) => {
              item.value = prvw_dynamic_quantity[prvw_i_state];
            });
          }
        });
        let pumperCustomQty = document.querySelector("#pumper_custom_qty");
        if (pumperCustomQty) {
          pumperCustomQty.value = prvw_dynamic_quantity[prvw_i_state];
        }
        if (document.getElementById("bottomtitle__total__price")) {
          if (prvw_dynamic_price[prvw_i_state] === "amount") {
            let price =
              document.getElementById("prvw_singleProductPrice").value / 100;

            let discount =
              prvw_dynamic_discount[prvw_i_state] *
              window.Shopify.currency.rate;

            price = price * prvw_dynamic_quantity[prvw_i_state] - discount;
            document
              .querySelectorAll(".bottomtitle__totalPrice__container")
              .forEach((item) => {
                item.innerHTML = formatAmount(price);
              });
          } else if (prvw_dynamic_price[prvw_i_state] === "percentage") {
            let price =
              document.getElementById("prvw_singleProductPrice").value / 100;
            price =
              price * prvw_dynamic_quantity[prvw_i_state] -
              price *
                prvw_dynamic_quantity[prvw_i_state] *
                (prvw_dynamic_discount[prvw_i_state] / 100);

            document
              .querySelectorAll(".bottomtitle__totalPrice__container")
              .forEach((item) => {
                item.innerHTML = formatAmount(price, "percentage");
              });
          } else if (prvw_dynamic_price[prvw_i_state] === "each") {
            let price =
              document.getElementById("prvw_singleProductPrice").value / 100;
            let discount =
              (
                window.Shopify.currency.rate *
                prvw_dynamic_discount[prvw_i_state]
              ).toFixed(2) * prvw_dynamic_quantity[prvw_i_state];
            price = price * prvw_dynamic_quantity[prvw_i_state] - discount;
            document
              .querySelectorAll(".bottomtitle__totalPrice__container")
              .forEach((item) => {
                item.innerHTML = formatAmount(price);
              });
          } else if (prvw_dynamic_price[prvw_i_state] === "fixed") {
            price =
              prvw_dynamic_discount[prvw_i_state] *
              window.Shopify.currency.rate;
            document
              .querySelectorAll(".bottomtitle__totalPrice__container")
              .forEach((item) => {
                item.innerHTML = formatAmount(price);
              });
          } else {
            let price =
              document.getElementById("prvw_singleProductPrice").value / 100;
            price = price * prvw_dynamic_quantity[prvw_i_state];

            document
              .querySelectorAll(".bottomtitle__totalPrice__container")
              .forEach((item) => {
                item.innerHTML = formatAmount(price);
              });
          }
        }

        isProductStockEmpty();

        logDebug("after without variant specic code in js file");
      }
      // sendBarImpression(value);
    }

    function setTotalPrice(amount) {
      document
        .querySelectorAll(".prvw_totalAmount_price_" + prvw_i_state)
        .forEach((item) => {
          item.innerHTML = amount;
        });
    }

    const formatAmount = function (cents, type) {
      if (typeof cents == "string") {
        cents = cents.replace(".", "");
      }
      var value = "";
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      let formatString = prvw_money_format;
      logDebug("formatString", formatString);
      function defaultOption(opt, def) {
        return typeof opt == "undefined" ? def : opt;
      }

      function ceilToTwoDecimalPlaces(num) {
        return Math.ceil(num * 100) / 100;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ",");
        decimal = defaultOption(decimal, ".");

        if (isNaN(number) || number == null) {
          return 0;
        }

        if (type === "percentage") {
          number = ceilToTwoDecimalPlaces(number).toFixed(precision);
        } else {
          number = number.toFixed(precision);
        }

        var parts = number.split("."),
          dollars = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            "$1" + thousands
          ),
          cents = parts[1] ? decimal + parts[1] : "";

        return dollars + cents;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    };

    /**
     * Updates prices across all quantity bars in the widget
     * Handles both variant and non-variant products
     */
    function changeWidgetPrice() {
      function priceByEachUnit(discountType, index) {
        const variantPrices = calculateVariantPrices(index);
        if (!variantPrices) return;

        const { totalOriginalAmount } = variantPrices;
        let finalPrice;

        switch (discountType) {
          case "fixed":
            finalPrice =
              prvw_dynamic_discount[index] * window.Shopify.currency.rate;
            break;

          case "amount":
            finalPrice =
              (totalOriginalAmount -
                prvw_dynamic_discount[index] * window.Shopify.currency.rate) /
              prvw_dynamic_quantity[index];
            break;

          case "percentage":
            finalPrice =
              (totalOriginalAmount -
                (totalOriginalAmount * prvw_dynamic_discount[index]) / 100) /
              prvw_dynamic_quantity[index];
            break;

          case "each":
            let discount =
              (
                window.Shopify.currency.rate * prvw_dynamic_discount[index]
              ).toFixed(2) * prvw_dynamic_quantity[index];

            finalPrice =
              (totalOriginalAmount - discount) / prvw_dynamic_quantity[index];
            break;

          default:
            finalPrice = totalOriginalAmount / prvw_dynamic_quantity[index];
        }

        updatePriceDisplay(index, finalPrice, discountType);
        updateCustomCompareAtPrice(index, finalPrice);
      }

      function notPriceByEachUnit(discountType, index) {
        const variantPrices = calculateVariantPrices(index);
        if (!variantPrices) return;

        const { totalOriginalAmount } = variantPrices;
        let finalPrice;

        switch (discountType) {
          case "fixed":
            finalPrice =
              prvw_dynamic_discount[index] * window.Shopify.currency.rate;
            break;

          case "amount":
            finalPrice =
              totalOriginalAmount -
              prvw_dynamic_discount[index] * window.Shopify.currency.rate;
            break;

          case "percentage":
            finalPrice =
              totalOriginalAmount -
              (totalOriginalAmount * prvw_dynamic_discount[index]) / 100;
            break;

          case "each":
            let discount =
              (
                window.Shopify.currency.rate * prvw_dynamic_discount[index]
              ).toFixed(2) * prvw_dynamic_quantity[index];

            finalPrice = totalOriginalAmount - discount;
            break;

          default:
            finalPrice = totalOriginalAmount;
        }

        updatePriceDisplay(index, finalPrice, discountType);
        updateCustomCompareAtPrice(index, finalPrice);
      }

      function updatePriceDisplay(index, price, discountType) {
        document
          .querySelectorAll(`.prvw_totalAmount_price_${index}`)
          .forEach((item) => {
            item.innerHTML = formatAmount(price, discountType);
          });
      }

      function updateCustomCompareAtPrice(index, finalPrice) {
        const customCompareAtPrice = document.getElementById(
          "prvw_customCompareAtPrice"
        ).value;
        if (customCompareAtPrice && customCompareAtPrice !== "0") {
          const compareAtPrice = formatAmount(
            customCompareAtPrice *
              window.Shopify.currency.rate *
              prvw_dynamic_quantity[index]
          );
          finalPrice = formatAmount(finalPrice);
          if (finalPrice != compareAtPrice) {
            document
              .querySelectorAll(`.prvw__pb__originalAmount${index}`)
              .forEach((item) => {
                item.innerHTML = compareAtPrice;
              });
          } else {
            document
              .querySelectorAll(`.prvw__pb__originalAmount${index}`)
              .forEach((item) => {
                item.innerHTML = "";
              });
          }
        }
      }

      function calculateVariantPrices(index) {
        try {
          let map = new Map();
          let totalComparePrice = 0;
          let totalOriginalAmount = 0;
          let iter = 0;
          let variant_values = "";

          document
            .querySelectorAll(`.prvw_selector_${index}`)
            .forEach((item, idx) => {
              variant_values += prvw_escape_html(item.value);
              let currOption = prvw_variant_map[variant_values];

              if (iter == variant_vals - 1) {
                if (!currOption?.compare_at_price) {
                  totalComparePrice += parseFloat(currOption?.price / 100);
                } else {
                  totalComparePrice += parseFloat(
                    currOption.compare_at_price / 100
                  );
                }
                totalOriginalAmount += parseFloat(currOption?.price / 100);

                iter = 0;
                map.set(currOption?.id, (map.get(currOption?.id) || 0) + 1);
                variant_values = "";
              } else {
                iter += 1;
              }
            });

          return { totalOriginalAmount, totalComparePrice, map };
        } catch (error) {
          console.error("Error calculating variant prices:", error);
          return null;
        }
      }

      function multipleVariants(discountType, index) {
        if (prvw_show_price_by_each === "true") {
          priceByEachUnit(discountType, index);
        } else {
          notPriceByEachUnit(discountType, index);
        }
      }

      function singleVariant(discountType, index) {
        const basePrice =
          document.getElementById("prvw_singleProductPrice").value / 100;
        const quantity = prvw_dynamic_quantity[index];
        let finalPrice;

        switch (discountType) {
          case "amount":
            finalPrice =
              basePrice * quantity -
              prvw_dynamic_discount[index] * window.Shopify.currency.rate;
            break;

          case "percentage":
            finalPrice =
              basePrice * quantity -
              (basePrice * quantity * prvw_dynamic_discount[index]) / 100;
            break;

          case "each":
            const discount =
              (
                window.Shopify.currency.rate * prvw_dynamic_discount[index]
              ).toFixed(2) * quantity;

            finalPrice = basePrice * quantity - discount;
            break;

          case "fixed":
            finalPrice =
              prvw_dynamic_discount[index] * window.Shopify.currency.rate;
            break;

          default:
            finalPrice = basePrice * quantity;
        }

        if (prvw_show_price_by_each === "true") {
          finalPrice = finalPrice / quantity;
        }

        updatePriceDisplay(index, finalPrice, discountType);
        updateCustomCompareAtPrice(index, finalPrice);
      }

      prvw_dynamic_price.forEach((discountType, index) => {
        if (checkIfVariantExists()) {
          multipleVariants(discountType, index);
        } else {
          singleVariant(discountType, index);
        }
      });
    }

    const onPrvwWindowLoadJS = () => {
      logDebug("onload function JS");

      let defaultSelectedBar = document.getElementById(
        "prvw_default_selected"
      ).value;

      if (document.getElementById(`cb${defaultSelectedBar}`)) {
        document.getElementById(`cb${defaultSelectedBar}`).checked = true;
      }
      prvw_variant_map = JSON.parse(
        document.getElementById("prvw_variant_map").textContent
      );
      prvw_variant_map2 = JSON.parse(
        document.getElementById("prvw_variant_map2").textContent
      );

      if (window.pmpr_useThemeFont_qb !== "true") {
        let selectors = [
          ".prvw_block"
          // ".block__title"
          // ".prvw_block h3",
          // ".prvw_block h4",
          // ".prvw_block h5",
          // ".prvw_block h6",
          // ".prevprice span",
          // ".prvw_totalAmount_wrapper span",
          // ".template_4_prevprice",
          // ".block__cbinfo--item",
          // ".block__cbinfo--dropdown select"
        ];

        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((item) => {
            item.style.fontFamily =
              '-apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI",Roboto, "Helvetica Neue", sans-serif';
          });
        });
      }

      //before variant specific javascript
      logDebug("before variant specific javascript");

      if (checkIfVariantExists()) {
        prvw_variant_tags.forEach((item, index) => {
          let prvw_variant_tag = document.querySelector(item.variantInput);
          if (prvw_variant_tag) {
            prvw_variant_tag.remove();
          }
        });

        document
          .querySelectorAll("[data-shopify=payment-button]")
          .forEach((item) => {
            item.type = "button";
            item.addEventListener(
              "click",
              (e) => {
                e.stopPropagation();
                pmpr_add_to_checkout();
              },
              { capture: true }
            );
          });

        document
          .querySelectorAll(".prvw_selector_custom")
          .forEach(function (item) {
            item.addEventListener("change", function (e) {
              prvw_radio_control(
                prvw_id_state,
                prvw_value_state,
                this,
                true,
                e
              );
            });
          });
      }

      //   let prvw_forms = document.querySelectorAll("form[action='/cart/add']");
      //   if (!prvw_forms.length) {
      //     prvw_forms = document.querySelectorAll("form[action*='/cart/add']");
      //   }

      if (skipToCheckout === "true" || window.upcartMoneyFormat) {
        prvw_forms.forEach(function (item) {
          item.addEventListener("submit", function (event) {
            event.preventDefault();
            if (checkIfVariantExists()) {
              prvw_custom_cart_add();
            } else {
              prvw_custom_cart_add_without_variant();
            }
          });
          let prvw_button;
          try {
            prvw_button = document.querySelector(
              "#" + item.getAttribute("id") + " button"
            );
          } catch (e) {
            console.log(e);
          }
          if (prvw_button) {
            window.upcartShouldSkipAddToCartInterceptor = true;

            if (skipToCheckout === "true") {
              window.upcartShouldSkipAddToCart = true;
              window.OpusNoATC = true;
            }

            prvw_button.type = "button";
            prvw_button.addEventListener("click", (e) => {
              e.preventDefault();
              if (checkIfVariantExists()) {
                prvw_custom_cart_add();
              } else {
                prvw_custom_cart_add_without_variant();
              }
            });
          }
        });
      }

      logDebug("after variant specific javascript");
      document.querySelectorAll(".prvw_pair").forEach(function (item) {
        item.addEventListener("change", function () {
          prvw_radio_control(prvw_id_state, this.value, this.id, false);
        });
      });

      prvw_radio_control(
        defaultSelectedBar,
        defaultSelectedBar,
        document.querySelector(`.prvw__quantity_input${defaultSelectedBar}`).id,
        false
      );

      logDebug("before showing the container");

      logDebug("after showing the container");

      changeWidgetPrice();

      // sendImpressions();

      // if (accessibleThemes.includes(userThemeName)) {
      //   let isOurVariantsExists = checkIfVariantExists();
      //   if (isOurVariantsExists) {
      //     matchVariantImageOnLoad();
      //   }
      // }
      matchVariantImageOnLoad();
      //   isVarientStockEmpty();
    };

    onPrvwWindowLoadJS();

    function prvw_escape_html(htmlStr) {
      console.log("htmlStr", htmlStr);
      const tempElement = document.createElement("textarea");
      tempElement.innerHTML = htmlStr;
      return tempElement.value || tempElement.innerText;

      // let finalStr = htmlStr.replace(/&/g, "&amp;");
      // finalStr = finalStr.replace(/</g, "&lt;");
      // finalStr = finalStr.replace(/>/g, "&gt;");
      // return finalStr;
    }

    function isVarientStockEmpty() {
      //tempObj ->  this object will store the stock quantity for all variants in {variant_name : quantity} key pair
      //outOfStock ->  outOfStock is used to determine whether there is any variant which is out of stock
      //prvw_value_state -> the current active offer index
      //splitArr ->  the value will determine how many times we have to iterate to iterate through all the select options
      //prvw_variant_map -> it has all the variant related info

      let tempObj = {};
      let outOfStock = false;

      // we have different functionality for a single variant and more than one variant
      if (variant_vals > 1) {
        // iterating through the prvw_variant_map
        // storing the variant name and quantity for all the variants in tempObj

        for (const item in prvw_variant_map) {
          if (prvw_variant_map[item].inv_m === "") {
            continue;
          }

          // checking if continue even if out of stock option is enabled
          if (prvw_variant_map[item].inv_p === "deny") {
            tempObj[item] = prvw_variant_map[item].quantity - 0;
          }
        }

        // now we have stock quantity of varient stored in tempObj
        // now just iterate through the number of select option we have and decrease the quantity by one for that varient option in tempObj
        // i.e if blue variant has 3 stock quantity then make it 2 in tempObj only
        // now check is the count for that varient is 0 if 0 then give that option red value otherwise the default value

        for (let i = 0; i < prvw_dynamic_quantity[prvw_value_state - 1]; i++) {
          let tempStr = ""; // this will be a concated value for the row of select option for ex: xl red variants then tempStr would be -> xlred for a iteration

          // selecting all select option in a row

          const row = document.querySelectorAll(
            `.prvw_selector_${prvw_value_state - 1}_${i + 1}`
          );

          row.forEach((item) => {
            tempStr += prvw_escape_html(item.value); //concatenating the value
          });
          // checking if the variant name exists in tempObj
          if (tempObj.hasOwnProperty(tempStr)) {
            if (tempObj[tempStr] <= 0) {
              row.forEach((item) => {
                item.style.border = "1px solid red";
              });

              outOfStock = true;
            } else {
              row.forEach((item) => {
                item.style.border = "1px solid #d9d9d9";
              });

              tempObj[tempStr] = --tempObj[tempStr];
            }
          } else {
            row.forEach((item) => {
              item.style.border = "1px solid #d9d9d9";
            });
          }

          tempStr = "";
        }
      } else {
        for (const item in prvw_variant_map) {
          if (prvw_variant_map[item].inv_m === "") {
            continue;
          }

          if (prvw_variant_map[item].inv_p === "deny") {
            tempObj[item] = prvw_variant_map[item].quantity - 0;
          }
        }

        for (let i = 0; i < prvw_dynamic_quantity[prvw_value_state - 1]; i++) {
          const selectContainer = document.getElementById(
            `prvw_selector_${prvw_value_state - 1}_${i + 1}`
          );

          if (tempObj.hasOwnProperty(selectContainer.value)) {
            if (tempObj[selectContainer.value] <= 0) {
              selectContainer.style.border = "1px solid red";
              outOfStock = true;
            } else {
              selectContainer.style.border = "1px solid #d9d9d9";
              tempObj[selectContainer.value] = --tempObj[selectContainer.value];
            }
          }
        }
      }

      if (outOfStock || isVariantNotAvailable) {
        logDebug("out of stock", outOfStock);
        logDebug("isVariantNotAvailable", isVariantNotAvailable);
        if (window.pmpr_showOutOfStockText === "true") {
          toggleOutOfStockContainer(true, window.pmpr_outOfStockText);
        } else {
          toggleOutOfStockContainer(
            true,
            isVariantNotAvailable
              ? "Selected item(s) not available"
              : "Highlighted item(s) are out of stock!"
          );
        }
      } else {
        toggleOutOfStockContainer(false, "");
      }
    }

    function isProductStockEmpty() {
      let has_only_default_variant = document.getElementById(
        "prvw_has_only_default_variant"
      ).value;

      let variantToCheck, quantity;

      if (has_only_default_variant === "true") {
        variantToCheck = prvw_variant_map["Default Title"];
        quantity =
          document.getElementById("prvw_default_variant_quantity").value - 0;
      } else {
        const firstKey = Object.keys(prvw_variant_map)[0];
        variantToCheck = prvw_variant_map[firstKey];
        quantity = prvw_variant_map[firstKey].quantity - 0;
      }

      if (variantToCheck?.inv_m === "") {
        return;
      }

      if (!isNaN(quantity) && variantToCheck?.inv_p === "deny") {
        let currVal = prvw_dynamic_quantity[prvw_value_state - 1];
        if (currVal) {
          if (quantity <= 0) {
            window.pmpr_showOutOfStockText === "true"
              ? toggleOutOfStockContainer(true, window.pmpr_outOfStockText)
              : toggleOutOfStockContainer(
                  true,
                  "This product is out of stock!"
                );
          } else if (currVal - 0 > quantity) {
            window.pmpr_showOutOfStockText === "true"
              ? toggleOutOfStockContainer(true, window.pmpr_outOfStockText)
              : toggleOutOfStockContainer(
                  true,
                  `Only ${quantity} item left in stock!`
                );
          } else {
            toggleOutOfStockContainer(false, "");
          }
        }
      }
    }

    function toggleOutOfStockContainer(show, message) {
      for (let i = 1; i <= prvw_length_bar; i++) {
        document
          .getElementById(`prvw_outOfStock_${i}`)
          .classList.add("prvw_d-none");
      }
      if (show) {
        document
          .getElementById(`prvw_outOfStock_${prvw_value_state}`)
          .classList.remove("prvw_d-none");
        document.getElementById(
          `prvw_outOfStock_${prvw_value_state}`
        ).innerHTML = message;
      } else {
        document
          .getElementById(`prvw_outOfStock_${prvw_value_state}`)
          .classList.add("prvw_d-none");
      }
    }

    dontHideBranding();
  } catch (e) {
    console.log("No Selector found in pumper");
    console.log(e);
  }
})();

function decodeHTML(encodedStr) {
  const tempElement = document.createElement("textarea"); // or 'div'
  tempElement.innerHTML = encodedStr;
  return tempElement.value || tempElement.innerText;
}

function prvw_custom_cart_add() {
  let formData = {
    items: prvw_main_cart_items
  };

  let submit_url = window.Shopify.routes.root + "cart/add.js";

  const skipToCheckout = document.getElementById(
    "pumper_skip_to_checkout"
  ).value;

  let using_upcart = document.querySelector(
    '[data-upcart-cart-enabled="true"]'
  );

  fetch(submit_url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(formData)
  })
    .then((data) => {
      if (using_upcart && skipToCheckout !== "true") {
        return;
      }
      //   window.upcartRefreshCart();
      if (skipToCheckout == "true") {
        window.location.href = "/checkout";
      } else {
        window.location.href = "/cart";
      }
    })
    .catch((error) => {
      console.error("Error:", error);
    });
}

function pmpr_add_to_checkout() {
  let formData = {
    items: prvw_main_cart_items
  };

  let submit_url = window.Shopify.routes.root + "cart/add.js";

  fetch(submit_url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(formData)
  })
    .then((data) => {
      window.location.href = "/checkout";
    })
    .catch((error) => {
      console.error("Error:", error);
    });
}

function prvw_custom_cart_add_without_variant() {
  const prvw_variant_map = JSON.parse(
    document.getElementById("prvw_variant_map").textContent
  );
  const prvw_quantity = document.querySelector("#pumper_custom_qty").value;

  let formData;

  let has_only_default_variant = document.getElementById(
    "prvw_has_only_default_variant"
  ).value;

  if (has_only_default_variant === "true") {
    formData = {
      items: [
        {
          id: prvw_variant_map["Default Title"].id,
          quantity: prvw_quantity
        }
      ]
    };
  } else {
    const firstKey = Object.keys(prvw_variant_map)[0];

    formData = {
      items: [
        {
          id: prvw_variant_map[firstKey].id,
          quantity: prvw_quantity
        }
      ]
    };
  }

  let submit_url = window.Shopify.routes.root + "cart/add.js";

  const skipToCheckout = document.getElementById(
    "pumper_skip_to_checkout"
  ).value;

  let using_upcart = document.querySelector(
    '[data-upcart-cart-enabled="true"]'
  );

  fetch(submit_url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(formData)
  })
    .then(() => {
      if (using_upcart && skipToCheckout !== "true") {
        return;
      }
      if (skipToCheckout == "true") {
        window.location.href = "/checkout";
      } else {
        window.location.href = "/cart";
      }
    })
    .catch((error) => {
      console.error("Error:", error);
    });
}

function dontHideBranding() {
  try {
    const container_elements = document.querySelectorAll(
      ".pmpr__branding_link"
    );
    const anchor_elements = document.querySelectorAll(".prvw__branding_link");
    const targetElements = [...container_elements, ...anchor_elements];

    if (!targetElements || targetElements.length == 0) return;

    for (let i = 0; i < targetElements.length; i++) {
      let targetElement = targetElements[i];

      const observer = new MutationObserver(function (mutationsList) {
        mutationsList.forEach(function (mutation) {
          if (mutation.attributeName === "style") {
            const computedStyle = window.getComputedStyle(targetElement);
            // console.log(computedStyle.display);

            if (computedStyle.display === "none") {
              targetElement.style.setProperty("display", "block", "important");
              // console.log("Display changed to none, reverting it back.");
            }
            if (computedStyle.visibility === "hidden") {
              targetElement.style.setProperty(
                "visibility",
                "visible",
                "important"
              );
              // console.log("Visibility changed to hidden, reverting it back.");
            }
            if (computedStyle.opacity === "0") {
              targetElement.style.setProperty("opacity", "1", "important");
              // console.log("Opacity changed to 0, reverting it back.");
            }
          }
        });
      });
      // Observe changes to the 'style' attribute of the target element
      observer.observe(targetElement, {
        attributes: true,
        attributeFilter: ["style"]
      });
    }
  } catch (error) {
    console.log("error in dontHideBranding", error);
  }
}

// function sendImpressions() {
//   const shop = Shopify.shop;
//   const timestamp = new Date().getTime();
//   const rand = Math.round(Math.random() * 10000);
//   const offerName = document.getElementById("prvw_offerName").value;

//   const myHeaders = new Headers();
//   myHeaders.append("Content-Type", "application/json");

//   const raw = JSON.stringify({
//     shop: shop,
//     type: "widget",
//     offerName: offerName,
//     timestamp: timestamp + rand,
//   });
//   const requestOptions = {
//     method: "POST",
//     headers: myHeaders,
//     body: raw,
//     redirect: "follow",
//   };

//   fetch(
//     "https://wtw04tw4bh.execute-api.us-east-2.amazonaws.com/prod/impressions",
//     requestOptions
//   )
//     .then((response) => response.text())
//     .catch((error) => console.log("error", error));
// }

// function sendBarImpression(barId) {
//   const shop = Shopify.shop;
//   const timestamp = new Date().getTime();
//   const rand = Math.round(Math.random() * 10000);
//   const offerName = document.getElementById("prvw_offerName").value;

//   const myHeaders = new Headers();
//   myHeaders.append("Content-Type", "application/json");
//   const raw = JSON.stringify({
//     shop: shop,
//     type: "bar",
//     barId: barId,
//     offerName: offerName,
//     timestamp: timestamp + rand,
//   });
//   const requestOptions = {
//     method: "POST",
//     headers: myHeaders,
//     body: raw,
//     redirect: "follow",
//   };

//   fetch(
//     "https://wtw04tw4bh.execute-api.us-east-2.amazonaws.com/prod/impressions",
//     requestOptions
//   )
//     .then((response) => response.text())
//     .catch((error) => console.log("error", error));
// }
(function () {
  if (performance.navigation.type == 2) {
    location.reload(true);
  }
  /********************************* */
  // add container to the dom
  /********************************* */

  const bundle = document.getElementById("pmpr_bundle__main");
  let productId = document.getElementById("pumperProductIDBundle")?.value;
  const pageSelector = document.querySelector("pumper-bundle-combo");
  const prvw_custom_selector = document.getElementById(
    "prvw_bundles_custom_selector"
  )?.value;

  if (window.allOffers[0]?.settings?.useThemeFont !== true) {
    let selectors = [
      ".pmpr_bundle__header h2",
      ".pmpr_bundle__product_title h4",
      ".pmpr_bundle__product_price h1",
      ".pmpr_bundle__product_price p",
      ".pmpr_bundle__quantity",
      ".pmpr_bundle__custom_select select",
      ".pmpr_bundle__buy_all h2",
      ".pmpr_bundle__buy_all .pmpr_bundle__total_price h1",
      ".pmpr_bundle__buy_all .pmpr_bundle__total_price h3",
      ".pmpr_bundle__buy_all .pmpr_bundle__highlight_offer",
      ".pmpr_bundle__add_to_cart",
      ".pmpr_bundle__add_to_cart",
      ".pmpr_bundle__total_price"
    ];

    document.querySelectorAll(".pmpr_bundle__root").forEach((item) => {
      item.style.fontFamily =
        '-apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI",Roboto, "Helvetica Neue", sans-serif';
    });
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((item) => {
        item.style.fontFamily = "inherit";
      });
    });
  } else {
    let selectors = [
      ".pmpr_bundle__header h2",
      ".pmpr_bundle__product_title h4",
      ".pmpr_bundle__product_price h1",
      ".pmpr_bundle__product_price p",
      ".pmpr_bundle__quantity",
      ".pmpr_bundle__custom_select select",
      ".pmpr_bundle__buy_all h2",
      ".pmpr_bundle__buy_all .pmpr_bundle__total_price h1",
      ".pmpr_bundle__buy_all .pmpr_bundle__highlight_offer",
      ".pmpr_bundle__add_to_cart",
      ".pmpr_bundle__add_to_cart",
      ".pmpr_bundle__total_price"
    ];

    document.querySelectorAll(".pmpr_bundle__root").forEach((item) => {
      item.style.fontFamily = "inherit";
    });

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((item) => {
        // item.style.removeProperty("font-family");
        item.style.fontFamily = "";
      });
    });
  }

  dontHideBranding();

  function addCustomSelectorContainer() {
    if (pageSelector) {
      pageSelector.innerHTML = bundle.innerHTML;

      //   pageSelector.insertAdjacentHTML("beforebegin", bundle.innerHTML);
    } else if (!prvw_custom_selector) {
      for (let i = 0; i < prvw_tags.length; i++) {
        let insertBefore = document.querySelector(prvw_tags[i].insertBefore);
        if (
          insertBefore &&
          !insertBefore.classList.contains("pmpr_bundle_form")
        ) {
          insertBefore.insertAdjacentHTML("afterend", bundle.innerHTML);
          break;
        }
      }
    } else {
      try {
        let insertBefore = document.querySelector(prvw_custom_selector);
        insertBefore.insertAdjacentHTML("beforebegin", bundle.innerHTML);
      } catch (error) {
        console.log(error);
      }
    }
    bundle.remove();
  }

  /********************************* */
  // util functions
  /********************************* */

  const formatAmountSimple = function (value) {
    const moneyFormat = window.money_format;
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    return moneyFormat.replace(placeholderRegex, value);
  };

  const formatAmount = function (cents) {
    if (typeof cents == "string") {
      cents = cents.replace(".", "");
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    let formatString = window.money_format;

    function defaultOption(opt, def) {
      return typeof opt == "undefined" ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ",");
      decimal = defaultOption(decimal, ".");

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = Number(number).toFixed(precision);

      var parts = number.split("."),
        dollars = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands
        ),
        cents = parts[1] ? decimal + parts[1] : "";

      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

  /********************************* */
  // dom addition functions
  /********************************* */

  function setProductPrice() {
    const allOffers = window.allOffers;
    const allOffersDiv = document.querySelectorAll(".pmpr_bundle__root");

    function toggleOutOfStockContainer(show, message, currIndex, offerDiv) {
      let finalTxt;
      if (window.allOffers[0]?.settings.showOutOfStockText) {
        finalTxt = window.allOffers[0]?.settings.outOfStockText;
      } else {
        finalTxt = message;
      }

      // console.log(finalTxt);
      if (show) {
        offerDiv
          .querySelectorAll(`#prvw_bundle_outOfStock_${currIndex}`)
          .forEach((item) => {
            item.classList.remove("pmpr_d-none");
          });

        offerDiv.querySelectorAll(".pmpr_bundle__product").forEach((item) => {
          if (item.classList.contains(currIndex)) {
            item.style.border = "1px solid red";
          }
        });

        offerDiv
          .querySelectorAll(".pmpr_bundle__custom_select")
          .forEach((item) => {
            if (item.classList.contains(currIndex)) {
              item.style.border = "1px solid red";
            }
          });

        offerDiv
          .querySelectorAll(`#prvw_bundle_outOfStock_${currIndex}`)
          .forEach((item) => {
            item.innerHTML = finalTxt;
          });
      } else {
        let { borderColor } = window.allOffers[0].typoGraphy.productRow;

        offerDiv
          .querySelectorAll(`#prvw_bundle_outOfStock_${currIndex}`)
          .forEach((item) => {
            item.classList.add("pmpr_d-none");
          });

        offerDiv.querySelectorAll(".pmpr_bundle__product").forEach((item) => {
          if (item.classList.contains(currIndex)) {
            item.style.borderColor = borderColor;
          }
        });

        offerDiv
          .querySelectorAll(".pmpr_bundle__custom_select")
          .forEach((item) => {
            if (item.classList.contains(currIndex)) {
              item.style.border = "1px solid #c9c9c9";
            }
          });
      }
    }

    function checkOutOfStock(currIndex, selectedVariantId, offerDiv) {
      try {
        let productInventory = window.pmpr_bundle_product_quantity;
        let id = selectedVariantId;

        if (productInventory.hasOwnProperty(id)) {
          let { quantity, inv_m, inv_p } = productInventory[id];

          if (inv_m !== null && inv_p === "deny" && quantity <= 0) {
            toggleOutOfStockContainer(
              true,
              "Product out of stock",
              currIndex,
              offerDiv
            );
          } else {
            toggleOutOfStockContainer(false, "", currIndex, offerDiv);
          }
        }
        // console.log(product.variants[0].inventory_management);
        // console.log(product.variants[0].inventory_quantity);
      } catch (error) {
        console.log(error);
      }
    }

    allOffersDiv.forEach((offer) => {
      const allSelectors = offer.querySelectorAll(".pmpr_bundle__selector");

      const currOfferId = offer.classList[1];
      const currOffer = allOffers[currOfferId];
      const allProducts = currOffer.comboProducts;
      let allPrice = new Map();

      function getVariantPrice(variants, selectedVariantId, quantity) {
        let currPrice;

        variants.forEach((variant) => {
          if (variant.id == selectedVariantId) {
            let variantPrice = variant.price / 100;
            let variantCompareToPrice = variant.compare_at_price / 100;

            let price = variantPrice * quantity;
            let compareAtPrice = variantCompareToPrice * quantity;
            if (currOffer.discount.type === "eachUnit") {
              price =
                price -
                Number(
                  currOffer.discount.value * window.Shopify.currency.rate
                ) *
                  quantity;
            }

            currPrice = { price, compareAtPrice };
          }
        });
        return currPrice;
      }

      function updateTotalPrice(allPrice) {
        let totalPrice = 0;
        let compareAtPrice = 0;

        for (const value of allPrice.values()) {
          totalPrice += Number(value.price);
          compareAtPrice += Number(value.compareAtPrice);
        }

        if (currOffer.discount.type === "percent") {
          totalPrice =
            totalPrice - (totalPrice * Number(currOffer.discount.value)) / 100;
        }

        if (currOffer.discount.type === "flat") {
          totalPrice =
            totalPrice -
            Number(currOffer.discount.value * window.Shopify.currency.rate);
        }

        if (currOffer.discount.type === "specific") {
          totalPrice = Number(
            currOffer.discount.value * window.Shopify.currency.rate
          );
        }

        console.log(" -> Setting total Price", totalPrice);

        const totalPriceDiv = offer.querySelector(".pmpr_bundle__total_price");
        totalPriceDiv.querySelector("h1").innerHTML = formatAmount(totalPrice);
        if (currOffer.settings.isComparePrice) {
          let finalComparePrice =
            currOffer.comparePrice == 0
              ? compareAtPrice
              : currOffer.comparePrice * window.Shopify.currency.rate;
          totalPriceDiv.querySelector("h3").innerHTML = formatAmount(
            finalComparePrice - 0
          );
        }
      }

      function calculateOriginalTotal() {
        let originalTotalPrice = 0;
        const productData = window.bundle_products;
        for (let i = 0; i < productData.length; i++) {
          const product = productData[i];
          product.variants.forEach((variant) => {
            if (variant.id + "" === allSelectors[i].value) {
              originalTotalPrice += variant.price * allProducts[i].quantity;
            }
          });
        }
        return Math.round((originalTotalPrice / 100) * 100) / 100;
      }

      function updatePrice(selector, offerDiv, originalTotalPrice) {
        const selectedVariantId = selector.value;

        const parent = selector.closest(".pmpr_bundle__product");

        const currIndex = parent.classList[1];
        const currProduct = allProducts[currIndex];
        let currProductShopify = window.bundle_products[currIndex];

        let currPrice = getVariantPrice(
          currProductShopify.variants,
          selectedVariantId,
          currProduct.quantity,
          currOffer
        );

        const originalCurrPrice = currPrice.price;

        if (currOffer.discount.type === "percent") {
          currPrice.price =
            currPrice.price -
            (currPrice.price * Number(currOffer.discount.value)) / 100;
        }

        if (currOffer.discount.type === "flat") {
          currPrice.price =
            currPrice.price -
            (Number(currOffer.discount.value * window.Shopify.currency.rate) *
              originalCurrPrice) /
              originalTotalPrice;
        }

        if (currOffer.discount.type === "specific") {
          currPrice.price =
            (Number(currOffer.discount.value * window.Shopify.currency.rate) *
              originalCurrPrice) /
            originalTotalPrice;
        }

        const priceDiv = parent.querySelector(".pmpr_bundle__product_price");

        let currProductPrice = currOffer.settings.showEachUnitPrice
          ? currPrice.price / currProduct.quantity
          : currPrice.price;
        let currProductComparePrice = currOffer.settings.showEachUnitPrice
          ? currPrice.compareAtPrice / currProduct.quantity
          : currPrice.compareAtPrice;

        priceDiv.querySelector("h1").innerHTML = formatAmount(currProductPrice);
        if (currOffer.settings.isComparePrice && currProductComparePrice > 0) {
          priceDiv.querySelector("p").innerHTML = formatAmount(
            currProductComparePrice
          );
        }

        currPrice.price = originalCurrPrice;

        allPrice.set(currProduct.id, currPrice);
        updateTotalPrice(allPrice);

        checkOutOfStock(currIndex, selectedVariantId, offerDiv);
      }

      function updateAllPrices() {
        // Update prices for all selectors
        const originalTotalPrice = calculateOriginalTotal();
        allSelectors.forEach((selector) => {
          updatePrice(selector, offer, originalTotalPrice);
        });
      }
      updateAllPrices();
      allSelectors.forEach((selector) => {
        selector.addEventListener("change", () => {
          updateAllPrices();
        });
      });
    });
  }

  // function updateHighlightTag() {
  //   const allOffers = window.allOffers;
  //   const allOffersDiv = document.querySelectorAll('.pmpr_bundle__root');

  //   allOffersDiv.forEach((offer) => {
  //     const tag = offer.querySelector('.pmpr_bundle__highlight_offer');
  //     const currOfferId = offer.classList[1];
  //     const currOffer = allOffers[currOfferId];

  //     switch (currOffer.discount.type) {
  //       case 'percent':
  //         tag.textContent = currOffer.discount.value + '% OFF';
  //         break;

  //       case 'flat':
  //         tag.textContent =
  //           formatAmountSimple(currOffer.discount.value) + ' OFF';
  //         break;

  //       case 'eachUnit':
  //         tag.textContent = tag.textContent =
  //           formatAmountSimple(currOffer.discount.value) + ' OFF EACH';
  //         break;

  //       default:
  //         tag.textContent = 'Combo!';
  //         break;
  //     }
  //   });
  // }

  async function addToCart(offerDiv, btn) {
    const allOffers = window.allOffers;
    const currOfferId = offerDiv.classList[1];
    const currOffer = allOffers[currOfferId];
    const allProducts = currOffer.comboProducts;
    const buttonText = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<div class="pmpr_bundle__loader" style="border-right-color: ${currOffer.typoGraphy.cta.color}" ></div>`;
    const allSelectors = offerDiv.querySelectorAll(".pmpr_bundle__selector");

    let items = [];
    allSelectors.forEach((selector) => {
      let selectedVariantId = selector.value;
      const parent = selector.closest(".pmpr_bundle__product");
      let productInventory = window.pmpr_bundle_product_quantity;

      const currIndex = parent.classList[1];

      const currProduct = allProducts[currIndex];
      currProduct.variants = window.bundle_products[currIndex].variants;
      let id = selectedVariantId;

      if (productInventory.hasOwnProperty(id)) {
        let { quantity, inv_m, inv_p } = productInventory[id];
        if (inv_m !== null && inv_p === "deny" && quantity <= 0) {
        } else {
          console.log(currProduct.variants[0].id);
          console.log(selectedVariantId);
          items.push({
            id: currProduct.variants.find((v) => v.id == selectedVariantId).id,
            quantity: currProduct.quantity
          });
        }
      } else {
        items.push({
          id: currProduct.variants.find((v) => v.id == selectedVariantId).id,
          quantity: currProduct.quantity
        });
      }
    });

    let formData = { items };

    let submit_url = window.Shopify.routes.root + "cart/add.js";

    try {
      fetch(submit_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      })
        .then((res) => res.json())
        .then((res) => {
          btn.disabled = false;
          btn.innerHTML = buttonText;
          if (currOffer.settings.skipCartToCheckout) {
            window.location.href = window.Shopify.routes.root + "checkout";
          } else {
            window.location.href = window.Shopify.routes.root + "cart";
          }
        });
    } catch (error) {
      console.log(error);
      btn.disabled = false;
      btn.innerHTML = currOffer.typoGraphy.cta.text;
    }
  }

  function applyEventOnBtn() {
    const allOffersDiv = document.querySelectorAll(".pmpr_bundle__root");

    allOffersDiv.forEach((offer) => {
      const btn = offer.querySelector(".pmpr_bundle__add_to_cart");
      btn.addEventListener("click", () => addToCart(offer, btn));
    });
  }

  function updateProductTitle() {
    if (window.allOffers[0].settings.customProductTitle === true) {
      let products = window.allOffers[0].comboProducts;
      let titleContainer = document.querySelectorAll(
        ".pmpr_bundle__product_title h4"
      );

      products.map((item, index) => {
        titleContainer[index].innerHTML = item.title;
      });
    }
  }

  function updateBundleCTALoaderClassNames() {
    const loaderDiv = document.querySelector(
      ".pmpr_bundle_form form .loading__spinner"
    );

    loader_classes_bundle.forEach((item) => {
      if (!loaderDiv.classList.contains(item)) {
        loaderDiv.classList.add(item);
      }
    });
  }

  if (window.inActiveStatus === true) {
    document
      .querySelectorAll(".pmpr_bundle__inActive_state")
      .forEach((item) => {
        item.classList.remove("pmpr_d-none");
      });

    document.querySelectorAll(".pmpr_bundle__root").forEach((item) => {
      item.classList.add("pmpr_d-none");
    });
  }

  updateProductTitle();
  addCustomSelectorContainer();
  // updateHighlightTag();
  setProductPrice();
  applyEventOnBtn();
  updateBundleCTALoaderClassNames();
})();

